import React, { type ReactNode } from 'react';
import MuiBadge from '@mui/material/Badge';
import MuiDivider from '@mui/material/Divider';
import MuiDrawer, { type DrawerProps } from '@mui/material/Drawer';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { COUNT_CAP } from '@/shared/utils/countCap';
import Box from '../primitives/Box';
import Button, { type ButtonSize } from '../actions/Button';
import IconButton from '../primitives/IconButton';
import Stack from '../primitives/Stack';
import Typography from '../primitives/Typography';

export interface FilterDrawerTriggerProps {
  activeCount?: number;
  onClick: () => void;
  label?: string;
  size?: ButtonSize;
  /** Icon button. Label stays as the accessible name and tooltip. */
  iconOnly?: boolean;
}

/** Toolbar trigger: secondary button + badge for active filter count. */
export function FilterDrawerTrigger({
  activeCount = 0,
  onClick,
  label = 'Bộ lọc',
  size = 'medium',
  iconOnly = false,
}: FilterDrawerTriggerProps) {
  const iconSize = size === 'large' ? 'medium' : 'small';
  return (
    <MuiBadge
      badgeContent={activeCount}
      max={COUNT_CAP}
      color="primary"
      invisible={activeCount === 0}
      sx={
        iconOnly
          ? {
              '& .MuiBadge-badge': {
                fontSize: 10,
                height: 14,
                minWidth: 14,
                padding: '0 3px',
                lineHeight: '14px',
              },
            }
          : undefined
      }
    >
      {iconOnly ? (
        <IconButton
          size={size === 'large' ? 'large' : size === 'medium' ? 'medium' : 'small'}
          color="primary"
          onClick={onClick}
          aria-label={label}
          title={label}
        >
          <FilterAltIcon fontSize={iconSize} />
        </IconButton>
      ) : (
        <Button intent="secondary" size={size} startIcon={<FilterAltIcon />} onClick={onClick}>
          {label}
        </Button>
      )}
    </MuiBadge>
  );
}

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Custom footer; when omitted, reset/apply buttons are rendered if handlers are provided. */
  footer?: ReactNode;
  onReset?: () => void;
  onApply?: () => void;
  resetLabel?: string;
  applyLabel?: string;
  /** Paper width; default full on mobile, 360px from `sm` up. */
  width?: number | string | Record<string, number | string>;
  anchor?: DrawerProps['anchor'];
}

const DEFAULT_WIDTH = { xs: '100vw', sm: 360 } as const;

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  title = 'Bộ lọc',
  children,
  footer,
  onReset,
  onApply,
  resetLabel = 'Đặt lại',
  applyLabel = 'Áp dụng',
  width = DEFAULT_WIDTH,
  anchor = 'right',
}) => {
  const resolvedFooter =
    footer ??
    (onReset || onApply ? (
      <Stack direction="row" spacing={1} sx={{ p: 2 }}>
        {onReset && (
          <Button intent="secondary" fullWidth onClick={onReset}>
            {resetLabel}
          </Button>
        )}
        {onApply && (
          <Button intent="primary" fullWidth onClick={onApply}>
            {applyLabel}
          </Button>
        )}
      </Stack>
    ) : null);

  return (
    <MuiDrawer anchor={anchor} open={open} onClose={onClose}>
      <Box
        sx={{
          width,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        role="presentation"
      >
        <Box sx={{ px: 2, py: 2 }}>
          {typeof title === 'string' ? (
            <Typography variant="h6">{title}</Typography>
          ) : (
            title
          )}
        </Box>

        <MuiDivider />

        <Stack spacing={2.5} sx={{ px: 2, py: 2, flex: 1, overflow: 'auto' }}>
          {children}
        </Stack>

        {resolvedFooter && (
          <>
            <MuiDivider />
            {resolvedFooter}
          </>
        )}
      </Box>
    </MuiDrawer>
  );
};

export default FilterDrawer;
